import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useRafLoop } from 'react-use'
import { Range } from 'types'
import { ArtboardLoopRef } from './types'

type Props = {
    delay: number
    paused?: boolean
    frames: number
    trim?: Range
    onLoop?: (progress: number) => void
}

const ArtboardLoop = ({ delay, paused = false, frames, trim, onLoop }: Props, ref: ForwardedRef<ArtboardLoopRef>) => {
    const frameRef = useRef<number>(0)
    const timeRef = useRef<number>(0)
    const [progress, setProgress] = useState<number>(0)

    const [stopLoop, startLoop] = useRafLoop((time: number) => {
        const seconds = time / 1000

        if (!paused && frames && seconds - timeRef.current >= delay) {
            const maxFrame = Math.floor((trim?.end || 1) * frames)
            const minFrame = Math.floor((trim?.start || 0) * frames)
            const newFrame = (frameRef.current + 1) % maxFrame

            frameRef.current = Math.max(minFrame, Math.min(maxFrame, newFrame))

            const newProgress = frameRef.current / frames
            setProgress(newProgress)
            onLoop?.(newProgress)

            timeRef.current = seconds
        } else {
            onLoop?.(progress)
        }
    }, false)

    function pause() {
        stopLoop()
    }

    function play() {
        startLoop()
    }

    function scrub(progress: number) {
        frameRef.current = Math.floor(frames * progress)
        setProgress(progress)
    }

    useEffect(() => {
        play()

        return () => {
            pause()
        }
    }, [])

    useImperativeHandle(ref, () => ({
        paused,
        progress,
        pause,
        play,
        scrub,
    }))

    return null
}

export default forwardRef(ArtboardLoop)
